import _ from 'lodash'
import { JSONSchemaResolver } from '../resolvers/json-schema-resolver'
import { EdgeProps, EntitySchemaProps } from '../models/prop-constants'
import { Entity } from '../models/entity'
import { AbstractSettings } from './abstract-settings'
import { SchemaIds } from './schema'
import { Try } from '../helpers/utils'
import moment from 'moment'
import { ErrorSeverityMap } from './types/validation-error-types'

const DOCUMENT_SCHEMA_URI = '/1.0/entities/metadata/document.json'


/**
 * Reverse lookup: given a $ref, extract the definition type name (last segment).
 * Matches any schema's definitions, not just entity.json.
 * Examples:
 *   - '/1.0/entities/metadata/entity.json#/definitions/fileSet' -> 'fileSet'
 *   - '/schemas/document.json#/definitions/attachment' -> 'attachment'
 *   - '/schemas/document.json#/definitions/yms/attachment' -> 'attachment'
 */
export function getDefinitionTypeFromRef(ref: string): string | undefined {
  const match = ref.match(/#\/definitions\/(?:\w+\/)*(\w+)$/)
  return match ? match[1] : undefined
}

/**
 * Represents the outcome of a patch resolution for a single path during a three-way merge.
 */
type PatchResolution = {
  /**
   * The patch object that should be applied.
   * `null` indicates that no action is needed (i.e., the local change is kept).
   */
  patch: object | null

  /**
   * A string literal describing the reason for the merge decision.
   * - `identical`: Local and remote changes were the same.
   * - `no_local`: A remote change existed with no corresponding local change.
   * - `enriched`: The remote change was a superset (enrichment) of the local change.
   * - `remote_newer`: A direct conflict was resolved by taking the remote change (last write wins).
   * - `local_newer`: A direct conflict was resolved by keeping the local change (last write wins).
   */
  resolution: 'identical' | 'no_local' | 'enriched' | 'remote_newer' | 'local_newer'
}

// find distance of each mixins from 'entity.json'
export function getDistanceFromRoot(store, schema, visited) {
  if (visited[schema.uniqueId]) {
    return visited[schema.uniqueId]
  }
  visited[schema.uniqueId] = 1
  const allOfs = schema.get('allOf', [])
  const anyOfs = schema.get('anyOf', [])
  const parentIds = _.map(allOfs.concat(anyOfs), JSONSchemaResolver.REF_KEY)
  parentIds.forEach((parentId) => {
    const parentSchema = store.getRecord(parentId)
    if (parentSchema) {
      const newDist = getDistanceFromRoot(store, parentSchema, visited) + 1
      visited[schema.uniqueId] = Math.max(visited[schema.uniqueId], newDist)
    }
  })
  return visited[schema.uniqueId]
}

export function orderMixins(store, entity) {
  const visited = {}
  _.forEach(entity.schemas, (schema) => {
    getDistanceFromRoot(store, schema, visited)
  })
  const newOrder = _.sortBy(entity.activeMixins, [
    (mixin) => visited[mixin.entityId],
    (mixin) => mixin.displayName,
  ])
  entity.set('mixins.active', newOrder)
}

export const convertServerError = (error) => {
  const { code, statusCode, errors, message } = error.responseJSON ?? {}
  const formattedErrors: Record<string | undefined, string[]> = {}
  const errorSeverities: ErrorSeverityMap = {}
  _.forEach(errors, ({ field, message, severity }) => {
    formattedErrors[field] ||= []
    formattedErrors[field].push(message)
    errorSeverities[field] ||= []
    errorSeverities[field].push(severity)
  })
  return {
    code: code || statusCode,
    errors: formattedErrors,
    errorSeverities,
    message,
  }
}

/** Creates an {@link Error} from any kind of error, including structured server errors. */
export function getError(error: any) {
  if (error instanceof Error) {
    return error
  } else if (_.isString(error)) {
    return new Error(error)
  } else {
    const message =
      stringifyError(error) ||
      Try(
        () => JSON.stringify(error),
        () => 'Unknown error'
      )
    return new Error(message)
  }
}

export function stringifyError(error: any) {
  const serverError = convertServerError(error)
  return serverError.message ? serverError.message : stringifyServerError(serverError)
}

/** Join server errors into one string */
export function stringifyServerError(serverError, delimiter = '; ') {
  const errors = _.flatMap(serverError.errors, (errors, field) => {
    const joined = _.join(errors, ', ')
    return field && field !== 'undefined' ? `${field}: ${joined}` : joined
  })
  return _.join(errors, delimiter)
}

export const getDocumentSchemas = (store, schemas, platform, includeStoryboards = true) => {
  let documentSchemas = []
  let documentSchemasWithPriority = []
  _.forEach(schemas, (edge) => {
    if (platform && edge.platforms && !_.includes(edge.platforms, platform)) {
      return
    }
    if (edge._operation === 'DELETE') {
      return
    }

    const schema = store.getRecord(edge.entityId)
    const mixins = _.map(schema.allOf, '$ref')
    const isDocument = _.includes(mixins, DOCUMENT_SCHEMA_URI)
    const isAbstract = _.get(schema, 'metadata.isAbstract', false)
    const isStoryboardPlan = _.includes(
      _.map(schema.mixins.active, 'entityId'),
      SchemaIds.STORYBOARD_PLAN
    )
    // VD-10800 exclude storyboard plans from ES search if appropriate.
    if ((isDocument && !isAbstract) || (includeStoryboards && isStoryboardPlan)) {
      if (edge.orderPriority) {
        documentSchemasWithPriority.push({ schema, priority: edge.orderPriority })
      } else {
        documentSchemas.push(schema)
      }
    }
  })
  documentSchemas = _.sortBy(documentSchemas, 'title')
  documentSchemasWithPriority = _.sortBy(documentSchemasWithPriority, 'priority')
  const orderedDocumentSchemasWithPriority = _.map(
    documentSchemasWithPriority,
    (document) => document.schema
  )
  return _.concat(orderedDocumentSchemasWithPriority, documentSchemas)
}

export const createEdge = (entityId) => {
  return {
    [EdgeProps.ENTITY_ID]: entityId,
  }
}

export const createRef = (entitySchema: Entity) => {
  return {
    $ref: entitySchema.get(EntitySchemaProps.URI),
  }
}

export function dictFromPairs(pairsList) {
  const ret = {}

  _.each(pairsList, (pair) => (ret[pair[0]] = pair[1]))

  return ret
}

export function canEditFirmView(settings: AbstractSettings) {
  return settings.isAdmin || settings.isManager || settings.isFirmAdmin
}

export function canSetFirmDefaultView(settings: AbstractSettings) {
  return settings.isAdmin || settings.isFirmAdmin
}

/**
 * @returns a pre-order dfs traversal of a component ref tree.
 */
export function flattenRefTree(refTree: any): any[] {
  const dfs = (node: any, results = []) => {
    if (!node) {
      return results
    }
    if (node.ref) {
      results.push(node.ref)
    }
    for (const key in node) {
      if (node.hasOwnProperty(key) && key !== 'ref') {
        dfs(node[key], results)
      }
    }
    return results
  }
  return dfs(refTree)
}

/**
 * Creates a sortable string to represent a UI component instance in a view
 * tree. It replaces instances of "items" in a uiSchemaPath with the actual
 * array item index from the valuePath.
 */
export function reifyUiSchemaArrayIndices(
  entity: Entity,
  uiSchemaPath: string[],
  valuePath: string[]
): string {
  if (!_.isArray(uiSchemaPath) || !_.isArray(valuePath)) {
    return
  }

  const uiSchemaPathCopy = [...uiSchemaPath]

  for (const index in uiSchemaPathCopy) {
    const value = uiSchemaPathCopy[index]
    if (value === 'items') {
      const slice = uiSchemaPathCopy.slice(0, Number(index))
      const resolved = entity.resolveSubschemaByPath(slice)
      if (resolved?.schema?.value) {
        const resolvedValuePath = resolved.schema.value.split('.')
        const arrayIndex = valuePath[resolvedValuePath.length]
        if (_.isNumber(arrayIndex)) {
          // replace 'items' with the actual array item index
          uiSchemaPathCopy[index] = arrayIndex
        }
      }
    }
  }
  return _.join(uiSchemaPathCopy, '.')
}

/**
 * Convert json pointer format "/a/b/c/1" to object path "a.b.c.1"
 */
export function jsonPointerToObjectPath(value?: string) {
  return value?.replace(/\//g, '.')?.slice(1)?.replace(/\.-$/, '')
}

/**
 * Convert any path format to object path "a.b.c":
 * - JSON Pointer: "/a/b/c/1" → "a.b.c.1"
 * - JSON Path: "$.a.b.c" → "a.b.c"
 * - Object path: "a.b.c" → "a.b.c"
 */
export function toObjectPath(value?: string): string | undefined {
  if (!value) return value
  if (value.startsWith('/')) return jsonPointerToObjectPath(value)
  if (value.startsWith('$.')) return value.slice(2)
  if (value === '$') return ''
  return value
}

export function objectPathToJsonPointer(path: string): string {
  return '/' + path.split('.').join('/')
}

export interface IOption {
  entityType: string
  label: string
  namespace: string
  path: string
  propertyType: string
  definitionType?: string
  ref?: string
  enum?: Array<{ label: string; value: string }>
  // Ready-to-merge JSON Schema fragment included when withNestedSchema is enabled.
  // object/ref → { properties: {...} }
  // array-object/array-ref → { items: { type, properties: {...} } }
  nestedSchema?: Record<string, any>
}

/** All possible property types that can be extracted from a schema. */
export type PropertyType = 'string' | 'boolean' | 'number' | 'integer' | 'object' | 'edge' | 'ref'

/**
 * primitive scalars + entity edges.
 */
export const DEFAULT_PROPERTY_TYPES: PropertyType[] = ['string', 'boolean', 'number', 'integer', 'edge']

export function getOptionsFromSchema(
  schema,
  apis,
  resolver,
  options?: { includeTypes?: PropertyType[] }
) {
  const results = []
  const { namespace } = schema.metadata
  const namespaceProperties = _.get(schema, 'properties.' + namespace + '.properties')
  _.forEach(namespaceProperties, (prop, key) => {
    try {
      const option = getOptionFromProperty(schema, namespace, prop, key, apis, resolver, options)
      if (option) {
        results.push(option)
      }
    } catch (err) {
      console.error(`Error processing ${namespace}.${key}, please fix the schema.`)
    }
  })
  return results
}

/**
 * Recursively collects options from a schema up to a specified depth.
 */
export function getOptionsFromSchemaDeep(
  schema,
  apis,
  resolver,
  options?: any,
  maxDepth: number = 2,
  currentDepth: number = 1,
  currentPath?: string
): IOption[] {
  const results: IOption[] = []
  const { namespace } = schema.metadata || {}
  const pathPrefix = currentPath ?? namespace
  const namespaceProperties = _.get(schema, 'properties.' + namespace + '.properties', _.get(schema, 'properties'))
  _.forEach(namespaceProperties, (prop, key) => {
    try {
      const option = getOptionFromProperty(schema, pathPrefix, prop, key, apis, resolver, options)
      if (option && !_.isEmpty(option.label)) {
        results.push(option)
      }
      if (
        currentDepth < maxDepth &&
        ((prop.type === 'object' && prop.properties) || prop.$ref)
      ) {
        let nestedSchema = prop
        if (prop.$ref) {
          const { schema: refSchema } = resolver.resolveReference(schema, prop.$ref)
          nestedSchema = refSchema
        }
        const nestedPath = pathPrefix ? `${pathPrefix}.${key}` : key
        const nestedOptions = getOptionsFromSchemaDeep(
          nestedSchema,
          apis,
          resolver,
          options,
          maxDepth,
          currentDepth + 1,
          nestedPath
        )
        results.push(...nestedOptions)
      }
    } catch (err) {
      console.error(`Error processing ${namespace}.${key}, please fix the schema.`)
    }
  })
  return results
}


export function getOptionFromProperty(
  entitySchema,
  namespace: string,
  prop,
  propertyName: string,
  apis,
  resolver,
  options?: { includeTypes?: PropertyType[] }
): IOption {
  const includeTypes = options?.includeTypes ?? DEFAULT_PROPERTY_TYPES
  const property = ensurePropertyIsDenormalized(entitySchema, prop, resolver)

  if (property.type === 'array') {
    // array type
    const valueOption = getOptionFromProperty(
      entitySchema,
      namespace,
      property.items,
      propertyName,
      apis,
      resolver,
      options
    )
    if (valueOption) {
      // Wrap the inner nestedSchema as items so the consumer gets a ready-to-merge fragment
      const innerType = valueOption.propertyType === 'ref' ? 'object' : valueOption.propertyType
      return {
        ...valueOption,
        propertyType: `array-${valueOption.propertyType}`,
        label: property.label,
        ...(valueOption.nestedSchema
          ? { nestedSchema: { items: { type: innerType, ...valueOption.nestedSchema } } }
          : {}),
      }
    }
  } else if (
    includeTypes.includes(property.type) &&
    _.isEmpty(property.$ref) &&
    !(property.type === 'string' && property.format === 'date-time')
  ) {
    // scalar types
    return {
      entityType: property.entityType,
      enum: _.map(property.enum, (option) => ({ label: option, value: option })),
      label: property.label,
      namespace,
      path: namespace + '.' + propertyName,
      propertyType: property.type || 'edge',
      ...(property.type === 'object' && property.properties ? { nestedSchema: { properties: property.properties } } : {}),
    }
  } else if (
    property.$ref === '/1.0/entities/metadata/entity.json#/definitions/edge' &&
    includeTypes.includes('edge')
  ) {
    // edge type
    const resolvedEntitySchema = apis.getStore().getRecord(property.entityType)
    return {
      entityType: property.entityType,
      label: property.label ? property.label : resolvedEntitySchema.title,
      namespace,
      path: namespace + '.' + propertyName,
      propertyType: 'edge',
      ref: property.$ref,
    }
  } else if (property.$ref && includeTypes.includes('ref')) {
    // $ref type — ensurePropertyIsDenormalized already merged the resolved schema into property
    const definitionType = getDefinitionTypeFromRef(property.$ref)
    if (definitionType) {
      return {
        entityType: property.entityType,
        label: property.label,
        namespace,
        path: namespace + '.' + propertyName,
        propertyType: "ref",
        definitionType: definitionType,
        ref: property.$ref,
        ...(property.properties ? { nestedSchema: { properties: property.properties } } : {}),
      }
    }
  }
}

export function ensurePropertyIsDenormalized(entitySchema, property, resolver) {
  // no need to denormalize edges, we know how to handle them already
  if (property.$ref && property.$ref !== '/1.0/entities/metadata/entity.json#/definitions/edge') {
    const { schema } = resolver.resolveReference(entitySchema, property.$ref)
    return {
      ...schema,
      ...property,
    }
  } else {
    return property
  }
}

/**
 * For lodash _merge, if the source field is undefined, it essentially skips the property
 * and does not set the property to undefined in the destination object.
 * In this implementation, it will honor the "undefined" from the source
 */
export function mergeUndefined<T>(dest: T, src: Partial<T>, options: any = {}): T {
  const { mergeArray = false } = options
  for (const key in src) {
    const srcValue = src[key]
    if (srcValue !== undefined) {
      const destValue = dest[key]
      if (
        (_.isPlainObject(srcValue) && _.isPlainObject(destValue)) ||
        (_.isArray(destValue) && mergeArray)
      ) {
        dest[key] = mergeUndefined(destValue, srcValue, options)
      } else {
        dest[key] = srcValue
      }
    } else {
      dest[key] = undefined
    }
  }
  return dest
}

export type ThreeWayMergeOptions = {
  /**
   * When local and remote both change the same field, keep the local value instead of
   * comparing timestamps. Used when refreshing a record that still has unsaved edits, so
   * pulling in the server's view of untouched fields never overwrites what the user is typing.
   */
  preferLocalOnConflict?: boolean
  /**
   * Leave the record's unsaved-edits flag as-is after the merge. Merging server data into a
   * record isn't the same thing as the user having saved it.
   */
  preserveDirt?: boolean
}

/**
 * Performs a three-way merge of an entity's content. It reconciles local changes
 * and remote changes against a common original version ('origContent').
 *
 * The merge strategy is as follows:
 * 1. Unchanged or local-only modifications are kept.
 * 2. New remote changes (additions, non-conflicting updates) are applied.
 * 3. Server-side enrichments (e.g., adding a 'displayName') are always applied.
 * 4. For direct conflicts, the version with the newer timestamp ("last write wins") is applied,
 *    unless `preferLocalOnConflict` is set.
 *
 * @returns {boolean} Returns `true` if any changes were merged into the entity, otherwise `false`.
 */
export function threeWayMerge(
  entity: Entity,
  remoteContent: any,
  logger: Function | null = null,
  options: ThreeWayMergeOptions = {}
) {
  const resolutions = {
    identical: [],
    no_local: [],
    enriched: [],
    remote_newer: [],
    local_newer: [],
  }
  const mergedPatches = []

  try {
    const excludedPaths = entity.isStoryboardExecution ? ['core_storyboard_execution.events'] : []
    const localPatches = entity.jsonPatch(entity.origContent, entity.content, { excludedPaths })
    const remotePatches = entity.jsonPatch(entity.origContent, remoteContent, { excludedPaths })

    const byPath: Map<string, { local?: any; remote?: any }> = new Map()
    localPatches.forEach((patch) => byPath.set(patch.path, { local: patch }))
    remotePatches.forEach((patch) =>
      byPath.set(patch.path, { ...byPath.get(patch.path), remote: patch })
    )

    const isRemoteNewer = !options.preferLocalOnConflict && entity.isOtherNewer(remoteContent)

    for (const { local, remote } of byPath.values()) {
      const { patch, resolution } = resolvePatch(local, remote, isRemoteNewer)

      if (patch && !_.isEmpty(patch)) {
        mergedPatches.push(patch)
      }
      resolutions[resolution].push({ path: local?.path || remote?.path, local, remote })
    }

    entity.applyPatch(mergedPatches)

    const hasChanges = !_.isEmpty(mergedPatches)

    if (hasChanges && !options.preserveDirt) {
      entity.clearDirt()
    }

    console.log('>>>DBG:mergeThreeWay:merged', entity.uniqueId, mergedPatches)
    console.log('>>>DBG:mergeThreeWay:resolutions', entity.uniqueId, resolutions)

    return hasChanges
  } catch (err) {
    logger?.('ThreeWayMergeError', {
      error: err.message,
      entityId: entity.uniqueId,
      resolutions,
      mergedPatches
    })
  }
}

/**
 * Checks if a list of resolutions is effectively empty.
 * A list is considered empty if it is actually empty, or if it contains only
 * a single, often-ignored resolution for the 'modifiedDate' path.
 */
function isEmptyResolutions(resolutions: any[]): boolean {
  return (
    _.isEmpty(resolutions) ||
    (_.size(resolutions) === 1 && _.first(resolutions)?.path === '/modifiedDate')
  )
}

/**
 * Patching resolution
 */
function resolvePatch(localPatch: any, remotePatch: any, isRemoteNewer: boolean): PatchResolution {
  // Case 1: No remote change, or changes are identical. Keep local state.
  if (!remotePatch || _.isEqual(localPatch, remotePatch)) {
    return { patch: null, resolution: 'identical' }
  }

  // Case 2: No local change; only a remote change exists. Apply remote.
  if (!localPatch) {
    return { patch: remotePatch, resolution: 'no_local' }
  }

  // Case 3: Both changed, but the remote change is a superset (enrichment). Apply remote.
  if (hasEnrichment(localPatch, remotePatch)) {
    return { patch: remotePatch, resolution: 'enriched' }
  }

  // Case 4: Direct conflict. Resolve using "last write wins".
  if (isRemoteNewer) {
    return { patch: remotePatch, resolution: 'remote_newer' }
  } else {
    // Local is newer, so its change wins by not applying any patch.
    return { patch: null, resolution: 'local_newer' }
  }
}

/**
 * Merges content based on the "last write wins" strategy, choosing the version
 * with the most recent 'modifiedDate'.
 *
 * This function is a critical part of data synchronization, ensuring that stale
 * data does not overwrite more recent changes, whether local or remote.
 *
 * @returns {boolean} Returns `true` if the remote content was applied, otherwise `false`.
 */
export function lastWriteWinsMerge(
  entity: Entity,
  remoteContent: object,
  logger: ((...args: any[]) => void) | null = console.warn
) {
  const localModifiedDate = entity.get('modifiedDate')
  const remoteModifiedDate = _.get(remoteContent, 'modifiedDate')

  // Determine if the remote content should be applied. The remote "wins" if:
  // 1. The local entity has no modification date (it's a new or unsynced entity).
  // 2. The remote date is the same as or later than the local date.
  const shouldApplyRemoteChanges =
    _.isNil(localModifiedDate) ||
    moment(localModifiedDate).isSameOrBefore(moment(remoteModifiedDate))

  if (shouldApplyRemoteChanges) {
    entity.setContent(remoteContent)
    entity.setPrevContent(remoteContent)

    return true
  } else {
    // The local copy is newer than the remote one. Do nothing and log the event.
    logger?.('[Entity::lastWriteWinsMerge] Local content is newer; skipping remote update.', {
      entityId: entity.uniqueId,
      localDate: localModifiedDate,
      remoteDate: remoteModifiedDate,
    })
  }
  return false
}

/**
 * Checks if any enrichment type is present by comparing local and remote patch operations.
 * It iterates through a set of specific enrichment checkers and returns true if any of them pass.
 */
const hasEnrichment = (local: any, remote: any): boolean => {
  const enrichmentChecks = {
    denormalized: hasDenormalizedEnrichment,
  }

  return _.some(enrichmentChecks, (checkFunc) => checkFunc(local, remote))
}

const hasDenormalizedEnrichment = (local: any, remote: any) => {
  if (local?.op !== remote?.op || remote?.op !== 'add') {
    return false
  }
  const remoteValue = remote?.value
  const enrichedProps = ['denormalizedProperties', 'displayName']
  return _.some(enrichedProps, (prop) => _.has(remoteValue, prop))
}
